import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ArrowRight,
  Sparkles,
  Image as ImageIcon,
  PenTool,
  Clapperboard,
  Menu,
  X,
  Minus,
  Plus,
  Info,
} from "lucide-react";
import {
  SiClaude,
  SiGooglegemini,
  SiGithubcopilot,
  SiPerplexity,
  SiNotion,
  SiElevenlabs,
  SiDeepseek,
  SiMistralai,
  SiHuggingface,
  SiFigma,
  SiCursor,
} from "react-icons/si";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import Nex3Logo from "@/components/site/Nex3Logo";

const API = `${process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "https://nex-3-api.vercel.app"}/api`;

const TOOLS = [
  { id: "chatgpt", name: "ChatGPT Plus", Icon: Sparkles, price: 20, bg: "#8FB39B", fg: "#12241B" },
  { id: "claude", name: "Claude Pro", Icon: SiClaude, price: 20, bg: "#C8724C", fg: "#241009" },
  { id: "gemini", name: "Gemini Advanced", Icon: SiGooglegemini, price: 20, bg: "#8AA0F0", fg: "#0B1533" },
  { id: "midjourney", name: "Midjourney", Icon: ImageIcon, price: 10, bg: "#EDEAE2", fg: "#1B1B1E" },
  { id: "copilot", name: "GitHub Copilot", Icon: SiGithubcopilot, price: 10, bg: "#9B95A8", fg: "#17151E" },
  { id: "perplexity", name: "Perplexity Pro", Icon: SiPerplexity, price: 20, bg: "#2E7D8A", fg: "#E7FBFF" },
  { id: "jasper", name: "Jasper", Icon: PenTool, price: 49, bg: "#8B5CF6", fg: "#160B2E" },
  { id: "notion", name: "Notion AI", Icon: SiNotion, price: 10, bg: "#D8D2C4", fg: "#1B1B1E" },
  { id: "elevenlabs", name: "ElevenLabs", Icon: SiElevenlabs, price: 22, bg: "#3A3A3A", fg: "#E7E7E7" },
  { id: "runway", name: "Runway", Icon: Clapperboard, price: 15, bg: "#4FE0B0", fg: "#08261C" },
  { id: "gamma", name: "Gamma", logo: "/images/ai-tools/gamma.svg", price: 10, bg: "#D9F1FF", fg: "#002253" },
  { id: "replit", name: "Replit", logo: "/images/ai-tools/replit.png", price: 20, bg: "#F4E8DE", fg: "#F26207" },
  { id: "grok", name: "Grok", logo: "/images/ai-tools/grok.svg", price: 30, bg: "#191919", fg: "#FCFCFC" },
  { id: "heygen", name: "HeyGen", logo: "/images/ai-tools/heygen.png", price: 29, bg: "#E7E7FF", fg: "#11111A" },
  { id: "otter", name: "Otter.ai", logo: "/images/ai-tools/otter.png", price: 17, bg: "#F4F7FF", fg: "#0866FF" },
  { id: "deepseek", name: "DeepSeek", Icon: SiDeepseek, price: 0, bg: "#1E293B", fg: "#06B6D4" },
  { id: "mistral", name: "Mistral AI", Icon: SiMistralai, price: 0, bg: "#1E1E1E", fg: "#F5F5F5" },
  { id: "copilot-pro", name: "Microsoft Copilot", logo: "/images/ai-tools/microsoft-copilot.svg", price: 20, bg: "#0078D4", fg: "#FFFFFF" },
  { id: "adobe-firefly", name: "Adobe Firefly", logo: "/images/ai-tools/adobe-firefly.svg", price: 20, bg: "#147EA9", fg: "#FFFFFF" },
  { id: "huggingface", name: "Hugging Face", Icon: SiHuggingface, price: 9, bg: "#FFD166", fg: "#000000" },
  { id: "figma", name: "Figma", Icon: SiFigma, price: 12, bg: "#F24E1E", fg: "#FFFFFF" },
  { id: "cursor", name: "Cursor", Icon: SiCursor, price: 20, bg: "#000000", fg: "#FFFFFF" },
  { id: "slack-ai", name: "Slack", logo: "/images/ai-tools/slack.svg", price: 6.67, bg: "#4A154B", fg: "#FFFFFF" },
];

function ToolLogo({ tool, className }) {
  if (tool.logo) {
    return <img src={tool.logo} alt="" aria-hidden="true" className={`${className} object-contain`} />;
  }

  const Icon = tool.Icon;
  return <Icon className={className} style={{ color: tool.fg }} />;
}

const COST_STEP = 20;

const AUDIT_OUTCOMES = [
  {
    id: "inventory",
    number: "01",
    title: "Inventory Current Spend",
    description: "Full audit of existing AI tools, subscriptions, and vendor contracts.",
  },
  {
    id: "overlap",
    number: "02",
    title: "Eliminate Waste & Overlap",
    description: "Identify redundant spend and consolidate for efficiency.",
  },
  {
    id: "roadmap",
    number: "03",
    title: "Build a 3-Year AI Roadmap",
    description: "Prioritized, high-ROI automation projects aligned to your business goals.",
  },
];

export default function Audit() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [audited, setAudited] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openCostInfo, setOpenCostInfo] = useState(null);
  const [activeOutcome, setActiveOutcome] = useState(null);
  const [toolCosts, setToolCosts] = useState(() =>
    Object.fromEntries(TOOLS.map((tool) => [tool.id, tool.price]))
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");

    document.title = "Free AI Budget Audit | NEX3 Inc.";
    description?.setAttribute(
      "content",
      "Compare the monthly cost of your AI subscriptions with a localized AI stack using NEX3's free AI budget audit."
    );

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) description.setAttribute("content", previousDescription);
    };
  }, []);

  useEffect(() => {
    (function initializeCal(C, A, L) {
      const push = (api, args) => api.q.push(args);
      const documentRef = C.document;

      C.Cal = C.Cal || function calEmbed() {
        const cal = C.Cal;
        const args = arguments;

        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          documentRef.head.appendChild(documentRef.createElement("script")).src = A;
          cal.loaded = true;
        }

        if (args[0] === L) {
          const api = function calNamespaceApi() {
            push(api, arguments);
          };
          const namespace = args[1];
          api.q = api.q || [];

          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            push(cal.ns[namespace], args);
            push(cal, ["initNamespace", namespace]);
          } else {
            push(cal, args);
          }
          return;
        }

        push(cal, args);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "30min", { origin: "https://app.cal.com" });
    window.Cal.config = window.Cal.config || {};
    window.Cal.config.forwardQueryParams = true;
    window.Cal.ns["30min"]("ui", {
      hideEventTypeDetails: false,
      layout: "month_view",
    });
  }, []);

  const toggle = (id) => {
    if (audited) return;
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const picked = useMemo(() => TOOLS.filter((t) => selected.includes(t.id)), [selected]);
  const currentCost = useMemo(
    () => picked.reduce((sum, tool) => sum + toolCosts[tool.id], 0),
    [picked, toolCosts]
  );
  const baselineCost = useMemo(
    () => picked.reduce((sum, tool) => sum + tool.price, 0),
    [picked]
  );
  const localizedCost = useMemo(() => {
    const baselineLocalizedCost = baselineCost * 0.35;
    const usageAdjustment = (currentCost - baselineCost) * 0.2;
    return Math.max(9, Math.round(baselineLocalizedCost + usageAdjustment));
  }, [baselineCost, currentCost]);
  const savedPct = useMemo(
    () => (currentCost ? Math.max(0, Math.round((1 - localizedCost / currentCost) * 100)) : 0),
    [currentCost, localizedCost]
  );
  const savedAmount = useMemo(() => Math.max(0, currentCost - localizedCost), [currentCost, localizedCost]);
  const localizedItems = useMemo(() => {
    const compute = Math.round(localizedCost * 0.6);
    const maintenance = localizedCost - compute;
    return [
      { label: "Self-hosted models", value: "$0" },
      {
        label: "Compute & hosting",
        value: `$${compute}`,
        description: "Estimated infrastructure for running your private AI: compute usage, secure hosting, storage, bandwidth, and monitoring.",
      },
      {
        label: "NEX3 maintenance",
        value: `$${maintenance}`,
        description: "Ongoing model and system updates, security patches, performance optimization, troubleshooting, and technical support.",
      },
    ];
  }, [localizedCost]);

  const runAudit = () => {
    if (selected.length === 0) return;
    setAudited(true);
    setTimeout(() => {
      const el = document.querySelector("[data-testid=audit-results]");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const adjustToolCost = (id, delta) => {
    setToolCosts((current) => ({
      ...current,
      [id]: Math.max(0, current[id] + delta),
    }));
  };

  const setToolCost = (id, value) => {
    const numericValue = String(value).replace(/[^0-9.]/g, "");
    const nextValue = Math.max(0, Number(numericValue) || 0);
    setToolCosts((current) => ({ ...current, [id]: nextValue }));
  };

  const celebrateRoadmap = (element) => {
    const bounds = element.getBoundingClientRect();
    const colors = ["#CDFF4E", "#00D7FF", "#087CFA", "#7C3CFF", "#FF3CC7"];
    const origin = {
      x: (bounds.left + bounds.width / 2) / window.innerWidth,
      y: (bounds.top + bounds.height * 0.58) / window.innerHeight,
    };
    const sharedOptions = {
      colors,
      disableForReducedMotion: true,
      ticks: 190,
      gravity: 1.05,
      decay: 0.92,
      scalar: 0.95,
      zIndex: 9999,
    };

    confetti({
      ...sharedOptions,
      particleCount: 65,
      angle: 58,
      spread: 72,
      startVelocity: 48,
      origin: { x: 0, y: 0.78 },
    });
    confetti({
      ...sharedOptions,
      particleCount: 65,
      angle: 122,
      spread: 72,
      startVelocity: 48,
      origin: { x: 1, y: 0.78 },
    });
    confetti({
      ...sharedOptions,
      particleCount: 90,
      spread: 105,
      startVelocity: 34,
      origin,
    });
  };

  const selectOutcome = (outcome, event) => {
    setActiveOutcome((current) => current === outcome.id ? null : outcome.id);
    if (outcome.id === "inventory") {
      document.querySelector("[data-testid=audit-page]")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (outcome.id === "roadmap") celebrateRoadmap(event.currentTarget);
  };

  const bookCall = () => {
    setMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 450);
  };

  const goApproach = () => {
    setMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#approach");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 450);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Name and a valid email are required.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/free-tools`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim() || "Get Free Tools Guide",
        referrer: document.referrer,
        landingPage: window.location.pathname,
        audit: {
          items: picked.map((tool) => ({
            name: tool.name,
            cost: toolCosts[tool.id],
          })),
          localizedCost,
        },
      });
      setSent(true);
      toast.success("Your bill summary and Free AI Guide have been sent.");
    } catch (error) {
      toast.error(error.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // "What comes next" — shown as a teaser before the audit, then below the cost comparison once the audit runs
  const outcomesSection = (
    <section className="audit-outcomes" aria-labelledby="audit-outcomes-title">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.08 }}
      >
        <span className="audit-outcomes__eyebrow">
          <span aria-hidden="true">{audited ? "✓" : "→"}</span>
          {audited ? "Audit complete" : "Your audit unlocks"}
        </span>
        <h2 id="audit-outcomes-title">Here&rsquo;s what comes next.</h2>
        <p>Three clear steps from insight to impact.</p>
      </motion.div>

      <div className="audit-outcomes__path">
        {AUDIT_OUTCOMES.map((outcome, index) => {
          const isActive = activeOutcome === outcome.id;
          return (
            <div className="audit-outcomes__path-item" key={outcome.id}>
              <motion.button
                type="button"
                data-testid={`audit-outcome-${outcome.id}`}
                data-cal-link={outcome.id === "overlap" ? "purmehdi/30min" : undefined}
                data-cal-namespace={outcome.id === "overlap" ? "30min" : undefined}
                data-cal-config={
                  outcome.id === "overlap"
                    ? '{"layout":"month_view","useSlotsViewOnSmallScreen":true}'
                    : undefined
                }
                aria-pressed={isActive}
                onClick={(event) => selectOutcome(outcome, event)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.18 + index * 0.12 }}
                whileTap={{ scale: 0.985 }}
                className={`audit-outcome ${isActive ? "is-active" : ""}`}
              >
                <span className="audit-outcome__fill" aria-hidden="true" />
                <span className="audit-outcome__content">
                  <span className="audit-outcome__number">{outcome.number}</span>
                  <strong>{outcome.title}</strong>
                  <span className="audit-outcome__description">{outcome.description}</span>
                  <span className="audit-outcome__hint">
                    {isActive ? "Selected" : "Explore step"}
                    <ArrowRight aria-hidden="true" />
                  </span>
                </span>
              </motion.button>

              {index < AUDIT_OUTCOMES.length - 1 && (
                <div className="audit-outcomes__connector" aria-hidden="true">
                  <span />
                  <ArrowRight />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <main data-testid="audit-page" className="relative min-h-screen bg-[var(--ink)] pb-28">
      {/* Top bar */}
      <header>
        <div className="mx-auto flex max-w-[1300px] items-center justify-between px-5 py-6 sm:px-10">
          <Link to="/" data-testid="audit-home" className="flex items-center gap-3">
            <Nex3Logo animate={false} className="h-4 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-[var(--muted)]">INC.</span>
          </Link>

          <nav className="hidden items-center gap-9 md:flex" aria-label="Audit page navigation">
            <button
              data-testid="audit-nav-approach"
              onClick={goApproach}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] transition-colors duration-300 hover:text-[var(--paper)]"
            >
              Approach
            </button>
            <Link
              to="/team"
              data-testid="audit-nav-team"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] transition-colors duration-300 hover:text-[var(--paper)]"
            >
              Team
            </Link>
            <Link
              to="/testimonials"
              data-testid="audit-nav-testimonials"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] transition-colors duration-300 hover:text-[var(--paper)]"
            >
              Testimonials
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="audit-book-call"
              onClick={bookCall}
              className="group relative hidden overflow-hidden rounded-full border border-[var(--paper)] px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] sm:block"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-[var(--ink)]">
                Book a Call
              </span>
              <span className="absolute inset-0 translate-y-full bg-[var(--acid)] transition-transform duration-300 group-hover:translate-y-0" />
            </button>
            <button
              type="button"
              data-testid="audit-mobile-toggle"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={menuOpen}
              aria-controls="audit-mobile-navigation"
              className="flex h-10 w-10 items-center justify-center rounded-full border hairline text-[var(--paper)] md:hidden"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              id="audit-mobile-navigation"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2 }}
              className="border-y hairline bg-[var(--ink)] px-5 py-5 shadow-2xl md:hidden"
              aria-label="Audit page mobile navigation"
            >
              <div className="mx-auto flex max-w-[1300px] flex-col">
                <button
                  onClick={goApproach}
                  className="border-b hairline py-4 text-left font-mono text-xs uppercase tracking-[0.22em] text-[var(--paper)]"
                >
                  Approach
                </button>
                <Link
                  to="/team"
                  onClick={() => setMenuOpen(false)}
                  className="border-b hairline py-4 text-left font-mono text-xs uppercase tracking-[0.22em] text-[var(--paper)]"
                >
                  Team
                </Link>
                <Link
                  to="/testimonials"
                  onClick={() => setMenuOpen(false)}
                  className="border-b hairline py-4 text-left font-mono text-xs uppercase tracking-[0.22em] text-[var(--paper)]"
                >
                  Testimonials
                </Link>
                <button
                  onClick={bookCall}
                  className="mt-5 rounded-full bg-[var(--acid)] px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink)] sm:hidden"
                >
                  Book a Call
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Hero */}
      <header className="border-b hairline pb-16 pt-10 text-center">
        <motion.p
          data-testid="audit-subheadline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-2xl font-semibold tracking-[-0.01em] sm:text-3xl lg:text-4xl"
        >
          Free <span className="text-[var(--acid)]">AI Budget</span> Audit
        </motion.p>
        <motion.h1
          data-testid="audit-headline"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-5 font-display text-5xl font-extrabold tracking-[-0.02em] sm:text-7xl lg:text-8xl"
        >
          Pick 3 AI You Pay For
        </motion.h1>
      </header>

      {/* Picker */}
      <section data-testid="tool-picker" className="mx-auto max-w-[1300px] px-5 pt-20 sm:px-10">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {TOOLS.map((t) => {
            const isSel = selected.includes(t.id);
            return (
              <button
                key={t.id}
                data-testid={`tool-${t.id}`}
                onClick={() => toggle(t.id)}
                disabled={audited}
                className={`relative flex flex-col items-center gap-4 rounded-2xl border p-6 transition-all duration-300 ${
                  isSel
                    ? "border-[var(--acid)] bg-[var(--ink-3)]"
                    : "hairline bg-[var(--ink-2)] hover:border-[var(--muted)]"
                } disabled:cursor-not-allowed`}
              >
                {isSel && (
                  <span
                    data-testid={`tool-check-${t.id}`}
                    className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--acid)]"
                  >
                    <Check className="h-3 w-3 text-[var(--ink)]" strokeWidth={3} />
                  </span>
                )}
                <span
                  className="flex h-14 w-14 items-center justify-center rounded-xl"
                  style={{ backgroundColor: t.bg, color: t.fg }}
                >
                  <ToolLogo tool={t} className="h-8 w-8" />
                </span>
                <span className="text-sm text-[var(--paper)]">{t.name}</span>
              </button>
            );
          })}
        </div>

        <p data-testid="selected-count" className="mt-10 text-center font-mono text-sm text-[var(--muted)]">
          <span className="text-[var(--acid)]">{selected.length}</span> selected
        </p>

        {/* Audit button — large & prominent, matching site buttons */}
        <div className="mt-10 flex flex-col items-center">
          <button
            data-testid="run-audit"
            onClick={runAudit}
            disabled={selected.length === 0 || audited}
            className={`group relative w-full max-w-xs overflow-hidden rounded-full px-10 py-5 font-mono text-sm uppercase tracking-[0.3em] transition-all duration-300 ${
              selected.length > 0 && !audited
                ? "bg-[var(--paper)] text-[var(--ink)]"
                : "cursor-not-allowed border border-[var(--line)] text-[var(--muted)]"
            }`}
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[var(--ink)]">
              Run the Audit
            </span>
            {selected.length > 0 && !audited && (
              <span className="absolute inset-0 translate-y-full bg-[var(--acid)] transition-transform duration-300 group-hover:translate-y-0" />
            )}
          </button>
          <p className="mt-4 font-mono text-xs tracking-wide text-[var(--muted)]">
            {audited
              ? "Audit complete"
              : selected.length > 0
              ? "Ready to audit"
              : "Pick 1 or more tools to run the audit"}
          </p>
        </div>

        {!audited && outcomesSection}
      </section>

      {/* Results — dark, on-theme */}
      <AnimatePresence>
        {audited && (
          <motion.section
            data-testid="audit-results"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-[1100px] px-5 pt-24 sm:px-10"
          >
            <div className="audit-results-label">
              <span>Cost comparison</span>
            </div>

            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-[1fr_auto_1fr]">
              {/* Your bill */}
              <div className="flex flex-col rounded-2xl border hairline bg-[var(--ink-2)] p-8">
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--muted)]">
                  Your Bill
                </div>
                <div className="mt-6 space-y-4 border-t border-dashed border-[var(--line)] pt-6">
                  {picked.map((t) => {
                    const cost = toolCosts[t.id];
                    return (
                      <div key={t.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-3 text-[var(--paper)]">
                          <span
                            className="flex h-7 w-7 items-center justify-center rounded-md"
                            style={{ backgroundColor: t.bg, color: t.fg }}
                          >
                            <ToolLogo tool={t} className="h-4 w-4" />
                          </span>
                          {t.name}
                        </span>
                        <div
                          className="grid grid-cols-[2rem_3.75rem_2rem] items-center gap-1.5"
                          aria-label={`${t.name} monthly cost`}
                        >
                          <button
                            type="button"
                            aria-label={`Decrease ${t.name} cost`}
                            disabled={cost === 0}
                            onClick={() => adjustToolCost(t.id, -COST_STEP)}
                            className="grid h-8 w-8 place-items-center rounded-full border hairline bg-[var(--ink-3)] text-[var(--paper)] transition-colors hover:border-[var(--muted)] disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <label className="font-mono text-[var(--paper)]">
                            <span className="sr-only">Set {t.name} monthly cost</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={cost === 0 ? "" : `$${cost}`}
                              onChange={(event) => setToolCost(t.id, event.target.value)}
                              className="w-[3.75rem] border-0 border-b border-transparent bg-transparent px-0.5 py-1 text-center font-mono text-sm text-[var(--paper)] outline-none transition-colors hover:border-[var(--line)] focus:border-[var(--acid)]"
                            />
                          </label>
                          <button
                            type="button"
                            aria-label={`Increase ${t.name} cost`}
                            onClick={() => adjustToolCost(t.id, COST_STEP)}
                            className="grid h-8 w-8 place-items-center rounded-full border hairline bg-[var(--ink-3)] text-[var(--paper)] transition-colors hover:border-[var(--muted)]"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-auto flex items-end justify-between border-t border-dashed border-[var(--line)] pt-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                    / month
                  </span>
                  <span data-testid="bill-total" className="font-display text-5xl font-extrabold text-[var(--paper)]">
                    ${currentCost}
                  </span>
                </div>
              </div>

              {/* Center — key savings figure */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.25 }}
                className="flex flex-row items-center justify-center gap-5 py-2 md:flex-col md:gap-6"
              >
                <ArrowRight className="h-10 w-10 text-[var(--acid)] md:h-12 md:w-12" strokeWidth={2.5} />
                <div
                  data-testid="saved-badge"
                  className="flex flex-col items-center rounded-3xl bg-[var(--acid)] px-7 py-6 text-center text-[var(--ink)] shadow-[0_0_50px_-8px_rgba(205,255,78,0.55)]"
                >
                  <span className="font-display text-5xl font-extrabold leading-none tracking-tight sm:text-6xl">
                    {savedPct}%
                  </span>
                  <span className="mt-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.3em]">
                    Saved
                  </span>
                  <span className="mt-3 rounded-full bg-black/10 px-3 py-1 font-mono text-xs font-semibold text-[var(--ink)]">
                    ${savedAmount} / mo
                  </span>
                </div>
              </motion.div>

              {/* Localized */}
              <div className="flex flex-col rounded-2xl border border-[var(--acid)] bg-[var(--ink-2)] p-8">
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--acid)]">
                  Localized AI
                </div>
                <div className="mt-6 space-y-4 border-t border-dashed border-[var(--line)] pt-6 text-sm">
                  {localizedItems.map((it) => (
                    <div key={it.label} className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2 text-[var(--paper)]">
                        {it.label}
                        {it.description && (
                          <span className="relative">
                            <button
                              type="button"
                              aria-label={`About ${it.label}`}
                              aria-expanded={openCostInfo === it.label}
                              onClick={() =>
                                setOpenCostInfo((current) => (current === it.label ? null : it.label))
                              }
                              className="grid h-5 w-5 place-items-center rounded-full border border-[var(--line)] text-[var(--muted)] transition-colors hover:border-[var(--acid)] hover:text-[var(--acid)] focus:border-[var(--acid)] focus:text-[var(--acid)] focus:outline-none"
                            >
                              <Info className="h-3 w-3" />
                            </button>
                            <AnimatePresence>
                              {openCostInfo === it.label && (
                                <motion.span
                                  role="tooltip"
                                  initial={{ opacity: 0, y: 5 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 5 }}
                                  className="absolute bottom-8 left-0 z-20 w-64 rounded-xl border border-[var(--line)] bg-[var(--ink)] p-3 text-left text-xs leading-relaxed text-[var(--paper)] shadow-2xl"
                                >
                                  {it.description}
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-[var(--paper)]">{it.value}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-auto flex items-end justify-between border-t border-dashed border-[var(--line)] pt-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
                    / month
                  </span>
                  <span data-testid="localized-total" className="font-display text-5xl font-extrabold text-[var(--acid)]">
                    ${localizedCost}
                  </span>
                </div>
              </div>
            </div>

            {outcomesSection}

            {/* Lead capture */}
            <div
              data-testid="audit-capture"
              className="mx-auto mt-16 max-w-3xl rounded-2xl border hairline bg-[var(--ink-2)] px-6 py-14 text-center sm:px-14"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--acid)]">
                Free Toolkit
              </span>
              <h3 className="font-display mt-5 text-3xl tracking-tight sm:text-4xl">
                Get your free AI tools
              </h3>
              <p className="mx-auto mt-4 max-w-md text-sm text-[var(--muted)] sm:text-base">
                We&rsquo;ll email your personalized bill summary and the Ultimate Guide to Free AI.
              </p>
              {sent ? (
                <p data-testid="audit-sent" className="mt-8 font-mono text-sm text-[var(--acid)]">
                  ✅ Thank you!<br />
                  Your personalized bill summary and Free AI Guide have been sent to your email.<br />
                  If you don&rsquo;t see it within a minute, please check your Spam or Promotions folder.
                </p>
              ) : (
                <form
                  onSubmit={submitEmail}
                  noValidate
                  data-testid="audit-form"
                  className="mx-auto mt-8 flex max-w-lg flex-col gap-3"
                >
                  <input
                    aria-label="Name"
                    value={form.name}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                    placeholder="Name *"
                    className="flex-1 rounded-full border hairline bg-[var(--ink)] px-6 py-4 text-sm text-[var(--paper)] placeholder:text-[var(--muted)] outline-none transition-colors duration-300 focus:border-[var(--acid)]"
                  />
                  <input data-testid="audit-email" aria-label="Email address" type="email" value={form.email} onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))} placeholder="Email *" className="flex-1 rounded-full border hairline bg-[var(--ink)] px-6 py-4 text-sm text-[var(--paper)] placeholder:text-[var(--muted)] outline-none transition-colors duration-300 focus:border-[var(--acid)]" />
                  <input aria-label="Company" value={form.company} onChange={(e) => setForm((current) => ({ ...current, company: e.target.value }))} placeholder="Company (optional)" className="flex-1 rounded-full border hairline bg-[var(--ink)] px-6 py-4 text-sm text-[var(--paper)] placeholder:text-[var(--muted)] outline-none transition-colors duration-300 focus:border-[var(--acid)]" />
                  <textarea aria-label="Message" value={form.message} onChange={(e) => setForm((current) => ({ ...current, message: e.target.value }))} placeholder="Message (optional)" className="min-h-28 flex-1 rounded-3xl border hairline bg-[var(--ink)] px-6 py-4 text-sm text-[var(--paper)] placeholder:text-[var(--muted)] outline-none transition-colors duration-300 focus:border-[var(--acid)]" />
                  <button
                    data-testid="audit-submit"
                    type="submit"
                    disabled={sending}
                    className="group relative overflow-hidden rounded-full bg-[var(--paper)] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink)] disabled:opacity-60"
                  >
                    <span className="relative z-10">{sending ? "Sending..." : "Send It Over"}</span>
                    <span className="absolute inset-0 translate-y-full bg-[var(--acid)] transition-transform duration-300 group-hover:translate-y-0" />
                  </button>
                </form>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <footer className="mt-24 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--muted)]">
        NEX3 Inc. · AI Cost Audit
      </footer>
    </main>
  );
}
